import { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import { useMutationPatchDatasetSchema } from "@/api/dataset/useDatasetAPI";
import {
  DatasetDetailRow,
  ExtendedColumnDef,
  generateColumns
} from "@/components/datasetManagement/details/columns";
import {
  BRING_YOUR_OWN_DATA_DATASET_ID,
  BRING_YOUR_OWN_DATA_MOCKS
} from "@/components/onboarding/configs/bring-your-own-data.mocks";
import { ROW_ID_DETAIL_DATASET } from "@/constants/dataset";
import { RootState } from "@/store";
import {
  DatasetAttributeSchema,
  DatasetData,
  DatasetMetadata,
  DatasetSchemaPayload
} from "@/types/dataset";

import { useDatasetStream } from "../useDatasetStream";

export interface IUseDetailDataset {
  schema?: DatasetAttributeSchema[];
  dynamicColumns: (
    handleEditRow: (row: DatasetDetailRow) => void,
    handleDeleteRows: (rowIndex: string) => void,
    isRowDatasetManagementEnabled: boolean,
    isEditDataGranted?: boolean,
    isDeleteDataGranted?: boolean
  ) => ExtendedColumnDef<DatasetDetailRow, unknown>[];
  metadata?: DatasetMetadata;
  data: DatasetDetailRow[];
  isLoading: boolean;
  datasetData?: DatasetData;
  highlightedRow: HighlightedRow | null;
  setHighlightedRow: (row: HighlightedRow | null) => void;
  setRefetchStream: React.Dispatch<React.SetStateAction<boolean>>;
}

export interface HighlightedRow {
  rowId?: string;
  mode?: "edit" | "add" | null;
  isHighlight?: boolean;
}

export const convertDatasetDataToTableData = (
  dataset?: DatasetData
): DatasetDetailRow[] => {
  if (!dataset?.rows || !dataset.columns) return [];

  return dataset.rows.map((row) => {
    const tableRow: DatasetDetailRow = {};
    row.forEach((value, index) => {
      const column = dataset.columns[index];

      if (column) tableRow[column.code] = value;
    });

    return tableRow;
  });
};

export const useDetailDataset = (): IUseDetailDataset => {
  const { id } = useParams();
  const isBringYourOwnDataOnboarding = useSelector(
    (state: RootState) => state.onboarding.active
  );
  const shouldUseOnboardingDataset =
    isBringYourOwnDataOnboarding && id === BRING_YOUR_OWN_DATA_DATASET_ID;

  const [isStreamLoading, setIsStreamLoading] = useState(false);
  const [datasetsData, setDatasetsData] = useState<DatasetData | undefined>();
  const [datasetsMetadata, setDatasetsMetadata] = useState<
    DatasetMetadata | undefined
  >();
  const [highlightedRow, setHighlightedRow] = useState<HighlightedRow | null>(
    shouldUseOnboardingDataset ? BRING_YOUR_OWN_DATA_MOCKS.highlightedRow : null
  );
  const [refetchStream, setRefetchStream] = useState(true);

  useDatasetStream({
    datasetId: id,
    enabled: !!id && refetchStream && !shouldUseOnboardingDataset,
    show_id: true,
    onOpen: () => {
      setIsStreamLoading(true);
    },
    onStreamComplete: (data) => {
      const updatedSchema = data.datasetMetadata.schema.map((column) => {
        // Check both attribute_name and schema_id because admin area's attributes can have the same id
        const metaDataData = data.datasetMetadataStream.columns.find(
          (schema) =>
            column.attribute_name === schema.name &&
            schema.schema_id === column.id
        );
        return {
          ...metaDataData,
          ...column,
          code: metaDataData?.code ?? "",
          id: metaDataData?.schema_id ?? column.id ?? ""
        } as DatasetAttributeSchema;
      });

      const updatedMetadata: DatasetMetadata = {
        ...data.datasetMetadata,
        schema: updatedSchema
      };

      setDatasetsMetadata(updatedMetadata);
      setDatasetsData(data.rawDataset);
      setRefetchStream(false);
      setIsStreamLoading(false);
    },
    onError: () => {
      setIsStreamLoading(false);
      setRefetchStream(false);
    }
  });

  const activeMetadata = shouldUseOnboardingDataset
    ? BRING_YOUR_OWN_DATA_MOCKS.datasetMetadata
    : datasetsMetadata;
  const activeDatasetData = shouldUseOnboardingDataset
    ? BRING_YOUR_OWN_DATA_MOCKS.datasetData
    : datasetsData;
  const activeHighlightedRow =
    shouldUseOnboardingDataset && !highlightedRow
      ? BRING_YOUR_OWN_DATA_MOCKS.highlightedRow
      : highlightedRow;

  const { mutate: mutatePatchDatasetSchema } = useMutationPatchDatasetSchema({
    onMutate: async (payload: DatasetSchemaPayload) => {
      if (datasetsMetadata && !shouldUseOnboardingDataset) {
        setDatasetsMetadata({
          ...datasetsMetadata,
          schema: datasetsMetadata?.schema.map((schema) =>
            schema.id === payload.id ? payload : { ...schema }
          ) as DatasetAttributeSchema[]
        });
      }

      return { previousMetadata: datasetsMetadata };
    },
    onError: (_err, _payload, context) => {
      if (context?.previousMetadata) {
        // Revert to previous metadata if an error occurs
        setDatasetsMetadata(context.previousMetadata);
      }
    },
    onSuccess: () => {
      // Refetch dataset detail only on success
      setRefetchStream(true);
    }
  });

  const dynamicColumns = (
    handleEditRow: (row: DatasetDetailRow) => void,
    handleDeleteRows: (rowIndex: string) => void,
    isRowDatasetManagementEnabled: boolean,
    isEditDataGranted: boolean = false,
    isDeleteDataGranted: boolean = false
  ) => {
    return activeMetadata?.schema
      ? generateColumns({
          columnsConfig: activeMetadata.schema,
          published: activeMetadata.published,
          mutatePatchDatasetSchema,
          handleEditRow,
          handleDeleteRows,
          isRowDatasetManagementEnabled,
          isEditDataGranted,
          isDeleteDataGranted
        })
      : [];
  };

  const data = useMemo(() => {
    const tempData = convertDatasetDataToTableData(activeDatasetData);

    if (
      activeHighlightedRow?.mode === "add" &&
      activeHighlightedRow?.rowId &&
      !isStreamLoading
    ) {
      const indexData = tempData.findIndex(
        (row) => row[ROW_ID_DETAIL_DATASET] === activeHighlightedRow.rowId
      );
      if (indexData < 0) return tempData;

      const newData = tempData.splice(indexData, 1);
      return [...newData, ...tempData];
    }

    return tempData;
  }, [activeDatasetData, activeHighlightedRow, isStreamLoading]);

  return {
    schema: activeMetadata?.schema,
    dynamicColumns,
    metadata: activeMetadata,
    data,
    datasetData: activeDatasetData,
    isLoading: shouldUseOnboardingDataset ? false : isStreamLoading,
    highlightedRow: activeHighlightedRow,
    setRefetchStream,
    setHighlightedRow
  };
};
